<script setup lang="ts">
import { computed } from 'vue';
import { useVModel } from '@vueuse/core';
import type { PossibleTypename } from '@precon-tools-suite-by-shared/precon-graphql-types';
import {
    EntityNameSpan,
    NameInput,
} from '@precon-tools-suite-by-shared/shared-names-ui';

export interface MaybeOwnedPredicate {
    id: string;
    __typename: PossibleTypename<'Predicate'>;
    predicateQuestion?: string;
    owner?: {
        name?: NameInput;
    };
}

const props = withDefaults(
    defineProps<{
        modelValue: string | null | undefined;
        options: MaybeOwnedPredicate[];
        showOwner?: boolean;
        loading?: boolean;
    }>(),
    {
        modelValue: null,
        options: () => [],
        showOwner: false,
        loading: false,
    }
);

const emit = defineEmits<{
    (e: 'update:modelValue', value: string | null): void;
}>();

const modelValue = useVModel(props, 'modelValue', emit);

const groupedOptions = computed(() =>
    props.options.reduce((result, item) => {
        const group = result.find((x) => x.label === item.__typename);

        if (group) {
            group.children.push(item);
        } else {
            result.push({ label: item.__typename, children: [item] });
        }

        return result;
    }, [] as Array<{ label: PossibleTypename<'Predicate'>; children: MaybeOwnedPredicate[] }>)
);
</script>
<template>
    <Dropdown
        v-model="modelValue"
        :options="groupedOptions"
        :loading="loading"
        option-group-label="label"
        option-group-children="children"
        option-value="id"
        class="question-dropdown"
    >
        <template #value="{ value }">
            <template v-if="value">
                <div class="flex justify-between w-full align-items-center">
                    <span
                        class="mr-auto white-space-normal"
                        v-html="
                            options.find((o) => o?.id === value)
                                ?.predicateQuestion
                        "
                    />
                    <EntityNameSpan
                        v-if="showOwner"
                        :node="value?.owner"
                        placeholder=""
                        class="ml-auto text-gray-300 white-space-nowrap"
                    />
                </div>
            </template>
            <template v-else> Selecteer een expressie </template>
        </template>
        <template #option="{ option }">
            <div class="flex justify-between w-full align-items-center">
                <span
                    class="mr-auto white-space-normal"
                    v-html="option.predicateQuestion"
                />
                <EntityNameSpan
                    v-if="showOwner"
                    :node="option?.owner"
                    placeholder=""
                    class="ml-auto text-gray-300 white-space-nowrap"
                />
            </div>
        </template>
    </Dropdown>
</template>
<style scoped lang="scss">
.question-dropdown {
    .p-dropdown-label {
        span {
            white-space: break-spaces;
        }
    }
}
.p-dropdown-items-wrapper {
    .p-dropdown-items {
        .p-dropdown-item {
            span {
                white-space: break-spaces;
            }
        }
    }
}
</style>
